import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const MySQLCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const mysqlHost = annotations['mysql.infrastructure/host'];
  const mysqlPort = annotations['mysql.infrastructure/port'];
  const mysqlDatabase = annotations['mysql.infrastructure/database'];
  const mysqlUser = annotations['mysql.infrastructure/user'];
  const mysqlPassword = annotations['mysql.infrastructure/password'];
  const mysqlVersion = annotations['mysql.infrastructure/version'];
  const cdcEnabled = annotations['mysql.infrastructure/cdc-enabled'];
  const binlogFormat = annotations['mysql.infrastructure/binlog-format'];
  const serverId = annotations['mysql.infrastructure/server-id'];
  const sshCommand = annotations['mysql.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded = annotations['mysql.infrastructure/ssh-private-key'];

  const hasMySQLCDCInfo = templateType === 'aws-mysql-cdc-source' && mysqlHost;
  if (!hasMySQLCDCInfo) return null;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'mysql_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const host = mysqlHost;
  const port = mysqlPort || '3306';
  const database = mysqlDatabase || 'sakila';
  const user = mysqlUser || 'rdi_user';
  const password = mysqlPassword || 'RedisCDC2024!';

  const cliCommand = `mysql -h ${host} -P ${port} -u ${user} -p${password} ${database}`;
  const rdiConfig = `connection:
  type: mysql
  host: ${host}
  port: ${port}
  database: ${database}
  user: ${user}
  password: ${password}
advanced:
  source:
    connector.class: io.debezium.connector.mysql.MySqlConnector
    database.server.id: ${serverId || '184054'}
    database.include.list: ${database}
    binlog.buffer.size: 8192
    snapshot.mode: initial`;

  return (
    <InfoCard title="MySQL CDC Source Information" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🐬 MySQL Configuration">
          {mysqlVersion && (
            <InfraField label="Version:" value={<InfraTagValue>{`MySQL ${mysqlVersion}`}</InfraTagValue>} mono={false} />
          )}
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={<InfraTagRow><InfraTag><RiCheckLine size={14} style={{ marginRight: 4 }} />Enabled</InfraTag></InfraTagRow>}
              mono={false}
            />
          )}
          {binlogFormat && (
            <InfraField label="Binlog Format:" value={<InfraTagValue>{binlogFormat}</InfraTagValue>} mono={false} />
          )}
          {serverId && <InfraField label="Server ID:" value={serverId} />}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          <CopyableValue label="Host:" value={host} />
          <InfraField label="Port:" value={port} />
          <InfraField label="Database:" value={database} />
          <InfraField label="User:" value={user} />
          {mysqlPassword && <CopyableValue label="Password:" value={mysqlPassword} />}
          <InfraField label="MySQL CLI Command:" value="" mono={false} />
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{cliCommand}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={cliCommand} />
            </div>
          </div>
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🚀 RDI Configuration">
          <Text variant="body-medium">Use this configuration snippet for Redis Data Integration:</Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfig}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfig} />
            </div>
          </div>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshCommand && (
                <>
                  <InfraField label="SSH Command:" value="" mono={false} />
                  <div style={{ position: 'relative' }}>
                    <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
                    <div style={{ position: 'absolute', top: 4, right: 4 }}>
                      <CopyButton text={sshCommand} />
                    </div>
                  </div>
                </>
              )}
              <InfraField label="SSH Private Key:" value="" mono={false} />
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button variant="secondary" size="small" onPress={() => navigator.clipboard.writeText(sshPrivateKey)}>
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>
              <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                💡 Save as mysql_key.pem and run: chmod 400 mysql_key.pem
              </Text>
            </InfraSection>
          </>
        )}
      </InfraStack>
    </InfoCard>
  );
};
